import { Box, SxProps, TextField, Theme, debounce } from "@mui/material";
import { ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { validateDocumentNumber } from "../documentsApi";
import { unwrapResult } from "@reduxjs/toolkit"
import { DocumentTypes } from "../documentTypes";
import { useLocalization } from "@fluent/react";
import { APIError } from "../../../app/appTypes";
import { loadCompanyDocumentNumberRules, selectCompanyDocumentNumberRules } from "../../administration/companyDocumentNumberRules/companyDocumentNumberRulesSlice";


interface DocNoTextFieldProps {
    value: string
    documentType: DocumentTypes
    onChange: (v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    sx?: SxProps<Theme>;
    label: ReactNode
    fullWidth?: boolean
    required?: boolean
    onErrorChange?: (hasError: boolean) => void;

}
export const DocumentNumberTextField = (props: DocNoTextFieldProps) => {
    const { sx, value, onChange, required, fullWidth, label } = props
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { items: numberRules, status: rulesStatus } = useAppSelector(selectCompanyDocumentNumberRules)

    const [status, setStatus] = useState<undefined | 'checking' | 'ok' | 'invalid'>(undefined)
    const [validationError, setValidationError] = useState<APIError | null>(null)
    const requestSeqRef = useRef(0)

    useEffect(() => {
        if (rulesStatus === "empty") void dispatch(loadCompanyDocumentNumberRules())
    }, [dispatch, rulesStatus])

    const activeRule = rulesStatus === "loaded"
        ? numberRules.find(rule => rule.isActive && rule.documentType === props.documentType)
        : undefined

    const check = useCallback(debounce((pattern) => {
        const trimmedPattern = pattern.trim()
        if (trimmedPattern === '') {
            setStatus(undefined)
            setValidationError(null)
            return
        }

        const requestId = ++requestSeqRef.current
        setStatus('checking')
        setValidationError(null)

        dispatch(validateDocumentNumber({ documentNumber: trimmedPattern, documentType: props.documentType }))
            .then(unwrapResult)
            .then(() => {
                if (requestSeqRef.current === requestId) {
                    setStatus('ok')
                }
            })
            .catch((err: APIError) => {
                if (requestSeqRef.current !== requestId) return

                if (err.kind === 'http' && err.problem) {
                    setStatus('invalid')
                    setValidationError(err)
                    return
                }

                console.error(err)
                setStatus(undefined)
                setValidationError(null)
            })
    }, 800), [dispatch, props.documentType])

    useEffect(() => {
        return () => {
            check.clear()
        }
    }, [check])

    const hasVisualError = (required ? value.trim() === '' : false) || status === 'invalid'
    const hasValidationPending = status === 'checking'
    const hasError = hasVisualError || hasValidationPending

    useEffect(() => { props.onErrorChange?.(hasError); }, [hasError, props.onErrorChange]);


    let helperText: ReactNode = <></>
    switch (status) {
        case 'checking':
            helperText = l10n.getString("document-number-checking", null, "_checking")
            break
        case 'invalid':
            helperText = validationError?.kind === 'http' && validationError.problem
                ? l10n.getString(validationError.problem.title, null, `_${validationError.problem.detail}`)
                : l10n.getString("error", null, "_Validation error")
            break
        case 'ok':
            helperText = ' '
            break
        default:
            helperText = l10n.getString("document-enter-number-prompt", null, "_prompt")
            break;
    }

    return <TextField
        value={value}
        onChange={e => {
            onChange(e)
            check(e.target.value)
        }}
        error={hasVisualError}
        helperText={
            <>
                <Box component="span" sx={{ display: "block" }}>{helperText}</Box>
                {status === 'invalid' && activeRule?.formatDescription && (
                    <Box component="span" sx={{ display: "block", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                        <Box component="strong">{l10n.getString("document-number-format-description", null, "_Opis formatu")}:</Box> {activeRule.formatDescription}
                    </Box>
                )}
                {status === 'invalid' && activeRule?.exampleNumber && (
                    <Box component="span" sx={{ display: "block", overflowWrap: "anywhere" }}>
                        <Box component="strong">{l10n.getString("document-number-example", null, "_Przykład")}:</Box> {activeRule.exampleNumber}
                    </Box>
                )}
                {rulesStatus === "error" && (
                    <Box component="span" sx={{ display: "block" }}>
                        {l10n.getString("document-number-rules-load-error", null, "_Nie udało się załadować wskazówek numeracji.")}
                    </Box>
                )}
            </>
        }
        required={required}
        fullWidth={fullWidth}
        label={label}
        sx={sx}
    />
}
